import { createApi } from '../client';
import { toQuery } from '../query';
import type { Paginated, SortDirection } from '../shared';

import type {
  AdjustmentInput,
  ContactDetail,
  ContactHistoryEntry,
  ContactImportPreview,
  ContactImportResult,
  ContactInput,
  ContactKind,
  ContactLabel,
  ContactNote,
  ContactPage,
  ContactSortKey,
  ContactType,
  AttachmentInput,
  IntegrationListing,
  ListingSelection,
  ProductListing,
  BatchInput,
  BillingOverview,
  Brand,
  BrandInput,
  BusinessAuthentication,
  Category,
  CategoryInput,
  Company,
  CompanyInput,
  DataTransfer,
  DataTransferItem,
  ConflictKind,
  ConflictSortKey,
  ConnectorPackage,
  ContentLanguages,
  CreateUserInput,
  CustomizationFieldInput,
  DownloadInput,
  DownloadLink,
  FeatureGroup,
  FilePurpose,
  FileUpload,
  FirstSyncDirection,
  GalleryImage,
  GalleryImageInput,
  Grant,
  IntegrationConflict,
  IntegrationLink,
  IntegrationLinkIssue,
  IntegrationLinkSettings,
  IntegrationLogEntry,
  IntegrationMapping,
  IntegrationPlatform,
  IssueInput,
  LicenceOverview,
  LogDirection,
  LogOutcome,
  LogSortKey,
  MappingDecision,
  MovementSortKey,
  OptionGroup,
  OrderShop,
  OrderSortKey,
  OrderStatus,
  PermissionDefinition,
  ProductAxesInput,
  ProductDetail,
  ProductInput,
  ProductRelationKind,
  ProductSortKey,
  ProductSummary,
  ProductType,
  ProductVisibility,
  ReceiptInput,
  RecordStatus,
  RelationsInput,
  ShopOrderDetail,
  ShopOrderSummary,
  StockMovement,
  StockMovementType,
  StockOperation,
  Tag,
  TenantUser,
  TenantUserProfile,
  TextChanges,
  TransferDirection,
  TransferItemMatch,
  TransferItemOutcome,
  TransferItemSortKey,
  TransferLimits,
  TransferMode,
  TransferSortKey,
  TransferStatus,
  TransferStockMode,
  UnlistedProduct,
  VariantInput,
  WriteOffInput,
} from './types';

/** One function per business-api endpoint (R0002 Notes/api-contract.md). */
const api = createApi('business');

const company = (companyId: string): string => `/companies/${encodeURIComponent(companyId)}`;

const product = (companyId: string, productId: string): string =>
  `${company(companyId)}/products/${encodeURIComponent(productId)}`;

export const login = (email: string, password: string): Promise<BusinessAuthentication> =>
  api.post<BusinessAuthentication>('/auth/login', { email, password });

export const readMe = (): Promise<TenantUserProfile> => api.get<TenantUserProfile>('/auth/me');

export const logout = (): Promise<{ revoked: number }> => api.post<{ revoked: number }>('/auth/logout', {});

export const listPermissions = (): Promise<Array<PermissionDefinition>> =>
  api.get<Array<PermissionDefinition>>('/permissions');

export const readLicence = (): Promise<LicenceOverview> => api.get<LicenceOverview>('/licence');

export const listCompanies = (): Promise<Array<Company>> => api.get<Array<Company>>('/companies');

export const createCompany = (input: CompanyInput): Promise<Company> =>
  api.post<Company>('/companies', input);

export const readCompany = (companyId: string): Promise<Company> => api.get<Company>(company(companyId));

export const updateCompany = (
  companyId: string,
  /** An omitted field is unchanged; `null` clears it. The currency is fixed at creation. */
  changes: { name?: string; country?: string } & Partial<
    Record<'registrationNumber' | 'taxNumber' | 'vatNumber' | 'street' | 'city' | 'postalCode', string | null>
  >,
): Promise<Company> => api.patch<Company>(company(companyId), changes);

export const readContentLanguages = (): Promise<ContentLanguages> =>
  api.get<ContentLanguages>('/settings/content-languages');

export const replaceContentLanguages = (languages: Array<string>): Promise<ContentLanguages> =>
  api.put<ContentLanguages>('/settings/content-languages', { languages });

export const readBilling = (): Promise<BillingOverview> => api.get<BillingOverview>('/billing');

export const chooseBillingCompany = (companyId: string): Promise<BillingOverview> =>
  api.put<BillingOverview>('/billing/company', { companyId });

export const downloadInvoicePdf = (invoiceId: string): Promise<Blob> =>
  api.blob(`/billing/invoices/${encodeURIComponent(invoiceId)}/pdf`);

export const archiveCompany = (companyId: string): Promise<Company> =>
  api.post<Company>(`${company(companyId)}/archive`, {});

export const restoreCompany = (companyId: string): Promise<Company> =>
  api.post<Company>(`${company(companyId)}/restore`, {});

export const listUsers = (): Promise<Array<TenantUser>> => api.get<Array<TenantUser>>('/users');

export const createUser = (input: CreateUserInput): Promise<TenantUser> =>
  api.post<TenantUser>('/users', input);

export const readUser = (userId: string): Promise<TenantUser> =>
  api.get<TenantUser>(`/users/${encodeURIComponent(userId)}`);

export const updateUser = (userId: string, displayName: string): Promise<TenantUser> =>
  api.patch<TenantUser>(`/users/${encodeURIComponent(userId)}`, { displayName });

export const replaceUserGrants = (userId: string, grants: Array<Grant>): Promise<TenantUser> =>
  api.put<TenantUser>(`/users/${encodeURIComponent(userId)}/permissions`, { grants });

export const deactivateUser = (userId: string): Promise<TenantUser> =>
  api.post<TenantUser>(`/users/${encodeURIComponent(userId)}/deactivate`, {});

export const reactivateUser = (userId: string): Promise<TenantUser> =>
  api.post<TenantUser>(`/users/${encodeURIComponent(userId)}/reactivate`, {});

export const resetUserPassword = (userId: string, password: string): Promise<{ revoked: number }> =>
  api.post<{ revoked: number }>(`/users/${encodeURIComponent(userId)}/password`, { password });

export const resendInvitation = (userId: string): Promise<void> =>
  api.post<void>(`/users/${encodeURIComponent(userId)}/invitation`, {});

/** Always accepted — the API never says whether the e-mail has an account. */
export const requestPasswordReset = (email: string): Promise<void> =>
  api.post<void>('/auth/password/forgot', { email });

export const setPasswordFromLink = (token: string, password: string): Promise<void> =>
  api.post<void>('/auth/password/set', { token, password });

export const searchProducts = (
  companyId: string,
  search: {
    term: string;
    status?: RecordStatus;
    visibility?: ProductVisibility;
    /** Only featured products, or only the others. */
    featured?: 'true' | 'false';
    vatRate?: number;
    /** In this category or any under it. */
    categoryId?: string;
    brandId?: string;
    tagId?: string;
    /** Only products of this type (R0030 WP-09). */
    type?: ProductType;
    page: number;
    sort: ProductSortKey;
    direction: SortDirection;
  },
): Promise<Paginated<ProductSummary>> =>
  api.get<Paginated<ProductSummary>>(`${company(companyId)}/products${toQuery(search)}`);

export const createProduct = (companyId: string, input: ProductInput): Promise<ProductDetail> =>
  api.post<ProductDetail>(`${company(companyId)}/products`, input);

export const readProduct = (companyId: string, productId: string): Promise<ProductDetail> =>
  api.get<ProductDetail>(product(companyId, productId));

export const updateProduct = (
  companyId: string,
  productId: string,
  changes: ProductInput,
): Promise<ProductDetail> => api.patch<ProductDetail>(product(companyId, productId), changes);

/** A new download's or attachment's bytes, uploaded through the API with their progress (R0030 WP-10, R0041). */
export const uploadFile = (
  companyId: string,
  file: File,
  purpose: FilePurpose,
  onProgress: (sent: number) => void,
): Promise<FileUpload> =>
  api.upload<FileUpload>(
    `${company(companyId)}/files/uploads${toQuery({ fileName: file.name, purpose })}`,
    file,
    onProgress,
  );

const productFiles = (companyId: string, productId: string, kind: 'downloads' | 'attachments'): string =>
  `${product(companyId, productId)}/${kind}`;

/** An uploaded file becomes the virtual product's last download. */
export const addDownload = (
  companyId: string,
  productId: string,
  input: DownloadInput & { fileId: string; fileName: string },
): Promise<ProductDetail> => api.post<ProductDetail>(productFiles(companyId, productId, 'downloads'), input);

export const updateDownload = (
  companyId: string,
  productId: string,
  downloadId: string,
  input: DownloadInput,
): Promise<ProductDetail> =>
  api.patch<ProductDetail>(
    `${productFiles(companyId, productId, 'downloads')}/${encodeURIComponent(downloadId)}`,
    input,
  );

/** An uploaded file becomes the product's last attachment. */
export const addAttachment = (
  companyId: string,
  productId: string,
  input: AttachmentInput & { fileId: string; fileName: string },
): Promise<ProductDetail> =>
  api.post<ProductDetail>(productFiles(companyId, productId, 'attachments'), input);

export const updateAttachment = (
  companyId: string,
  productId: string,
  attachmentId: string,
  input: AttachmentInput,
): Promise<ProductDetail> =>
  api.patch<ProductDetail>(
    `${productFiles(companyId, productId, 'attachments')}/${encodeURIComponent(attachmentId)}`,
    input,
  );

/** Removes a download or an attachment; its file goes from storage. */
export const removeProductFile = (
  companyId: string,
  productId: string,
  kind: 'downloads' | 'attachments',
  id: string,
): Promise<ProductDetail> =>
  api.delete<ProductDetail>(`${productFiles(companyId, productId, kind)}/${encodeURIComponent(id)}`);

export const reorderProductFiles = (
  companyId: string,
  productId: string,
  kind: 'downloads' | 'attachments',
  ids: Array<string>,
): Promise<ProductDetail> =>
  api.put<ProductDetail>(`${productFiles(companyId, productId, kind)}/order`, { ids });

/** A one-time link that downloads the file through the API (R0041). */
export const productFileLink = (
  companyId: string,
  productId: string,
  kind: 'downloads' | 'attachments',
  id: string,
): Promise<DownloadLink> =>
  api.get<DownloadLink>(`${productFiles(companyId, productId, kind)}/${encodeURIComponent(id)}/link`);

/** Replaces the product's customization fields, in order (R0030 WP-11). */
export const replaceCustomizationFields = (
  companyId: string,
  productId: string,
  fields: Array<CustomizationFieldInput>,
): Promise<ProductDetail> =>
  api.put<ProductDetail>(`${product(companyId, productId)}/customization-fields`, { fields });

/** Replaces the product's related products of one kind, in order (R0030 WP-08). */
export const replaceRelations = (
  companyId: string,
  productId: string,
  kind: ProductRelationKind,
  input: RelationsInput,
): Promise<ProductDetail> =>
  api.put<ProductDetail>(`${product(companyId, productId)}/relations/${kind}`, input);

export const archiveProduct = (companyId: string, productId: string): Promise<ProductDetail> =>
  api.post<ProductDetail>(`${product(companyId, productId)}/archive`, {});

export const restoreProduct = (companyId: string, productId: string): Promise<ProductDetail> =>
  api.post<ProductDetail>(`${product(companyId, productId)}/restore`, {});

export const createBatch = (
  companyId: string,
  productId: string,
  input: BatchInput,
): Promise<ProductDetail> => api.post<ProductDetail>(`${product(companyId, productId)}/batches`, input);

export const updateBatch = (
  companyId: string,
  productId: string,
  batchId: string,
  changes: { priceMinor?: number | null; purchasePriceMinor?: number | null },
): Promise<ProductDetail> =>
  api.patch<ProductDetail>(
    `${product(companyId, productId)}/batches/${encodeURIComponent(batchId)}`,
    changes,
  );

const optionGroup = (companyId: string, groupId: string): string =>
  `${company(companyId)}/option-groups/${encodeURIComponent(groupId)}`;

export const listOptionGroups = (companyId: string): Promise<Array<OptionGroup>> =>
  api.get<Array<OptionGroup>>(`${company(companyId)}/option-groups`);

const asTranslations = (
  texts: TextChanges | undefined,
  field: 'name' | 'value',
): Record<string, Record<string, string | null>> | undefined =>
  texts === undefined
    ? undefined
    : Object.fromEntries(Object.entries(texts).map(([locale, text]) => [locale, { [field]: text }]));

/** Names by language; the default language's is required. */
export const createOptionGroup = (companyId: string, names: TextChanges): Promise<OptionGroup> =>
  api.post<OptionGroup>(`${company(companyId)}/option-groups`, {
    translations: asTranslations(names, 'name'),
  });

export const updateOptionGroup = (
  companyId: string,
  groupId: string,
  changes: { names?: TextChanges; position?: number },
): Promise<OptionGroup> =>
  api.patch<OptionGroup>(optionGroup(companyId, groupId), {
    translations: asTranslations(changes.names, 'name'),
    position: changes.position,
  });

export const deleteOptionGroup = (companyId: string, groupId: string): Promise<void> =>
  api.delete<void>(optionGroup(companyId, groupId));

/** Texts by language; the default language's is required. */
export const createOptionValue = (
  companyId: string,
  groupId: string,
  texts: TextChanges,
): Promise<OptionGroup> =>
  api.post<OptionGroup>(`${optionGroup(companyId, groupId)}/values`, {
    translations: asTranslations(texts, 'value'),
  });

export const updateOptionValue = (
  companyId: string,
  groupId: string,
  valueId: string,
  changes: { texts?: TextChanges; position?: number },
): Promise<OptionGroup> =>
  api.patch<OptionGroup>(`${optionGroup(companyId, groupId)}/values/${encodeURIComponent(valueId)}`, {
    translations: asTranslations(changes.texts, 'value'),
    position: changes.position,
  });

export const deleteOptionValue = (
  companyId: string,
  groupId: string,
  valueId: string,
): Promise<OptionGroup> =>
  api.delete<OptionGroup>(`${optionGroup(companyId, groupId)}/values/${encodeURIComponent(valueId)}`);

export const setProductAxes = (
  companyId: string,
  productId: string,
  input: ProductAxesInput,
): Promise<ProductDetail> => api.put<ProductDetail>(`${product(companyId, productId)}/option-groups`, input);

export const createVariant = (
  companyId: string,
  productId: string,
  input: VariantInput,
): Promise<ProductDetail> => api.post<ProductDetail>(`${product(companyId, productId)}/variants`, input);

export const updateVariant = (
  companyId: string,
  productId: string,
  variantId: string,
  changes: VariantInput,
): Promise<ProductDetail> =>
  api.patch<ProductDetail>(
    `${product(companyId, productId)}/variants/${encodeURIComponent(variantId)}`,
    changes,
  );

/** Deleted when the variant has no batches, archived when it has. */
export const removeVariant = (
  companyId: string,
  productId: string,
  variantId: string,
): Promise<ProductDetail> =>
  api.delete<ProductDetail>(`${product(companyId, productId)}/variants/${encodeURIComponent(variantId)}`);

export const receiveStock = (companyId: string, input: ReceiptInput): Promise<StockOperation> =>
  api.post<StockOperation>(`${company(companyId)}/stock/receipts`, input);

export const issueStock = (companyId: string, input: IssueInput): Promise<StockOperation> =>
  api.post<StockOperation>(`${company(companyId)}/stock/issues`, input);

export const adjustStock = (companyId: string, input: AdjustmentInput): Promise<StockOperation> =>
  api.post<StockOperation>(`${company(companyId)}/stock/adjustments`, input);

export const writeOffStock = (companyId: string, input: WriteOffInput): Promise<StockOperation> =>
  api.post<StockOperation>(`${company(companyId)}/stock/write-offs`, input);

export const searchMovements = (
  companyId: string,
  search: {
    productId?: string;
    batchId?: string;
    type?: StockMovementType;
    page: number;
    sort: MovementSortKey;
    direction: SortDirection;
  },
): Promise<Paginated<StockMovement>> =>
  api.get<Paginated<StockMovement>>(`${company(companyId)}/stock/movements${toQuery(search)}`);

const link = (companyId: string, linkId: string): string =>
  `${company(companyId)}/integrations/${encodeURIComponent(linkId)}`;

export const listConnectorPackages = (companyId: string): Promise<Array<ConnectorPackage>> =>
  api.get<Array<ConnectorPackage>>(`${company(companyId)}/connectors`);

export const downloadConnectorPackage = (companyId: string, file: string): Promise<Blob> =>
  api.blob(`${company(companyId)}/connectors/${encodeURIComponent(file)}`);

export const listIntegrationLinks = (companyId: string): Promise<Array<IntegrationLink>> =>
  api.get<Array<IntegrationLink>>(`${company(companyId)}/integrations`);

export const createIntegrationLink = ({
  companyId,
  ...input
}: IntegrationLinkSettings & {
  companyId: string;
  platform: IntegrationPlatform;
}): Promise<IntegrationLinkIssue> =>
  api.post<IntegrationLinkIssue>(`${company(companyId)}/integrations`, input);

export const readIntegrationLink = (companyId: string, linkId: string): Promise<IntegrationLink> =>
  api.get<IntegrationLink>(link(companyId, linkId));

export const updateIntegrationLink = ({
  companyId,
  linkId,
  ...settings
}: IntegrationLinkSettings & { companyId: string; linkId: string }): Promise<IntegrationLink> =>
  api.patch<IntegrationLink>(link(companyId, linkId), settings);

export const reissueConnectionToken = ({ companyId, linkId }: { companyId: string; linkId: string }) =>
  api.post<IntegrationLinkIssue>(`${link(companyId, linkId)}/token`, {});

export const revokeIntegrationLink = ({ companyId, linkId }: { companyId: string; linkId: string }) =>
  api.post<IntegrationLink>(`${link(companyId, linkId)}/revoke`, {});

export const setIntegrationLinkEnabled = ({
  companyId,
  linkId,
  enabled,
}: {
  companyId: string;
  linkId: string;
  enabled: boolean;
}): Promise<IntegrationLink> =>
  api.post<IntegrationLink>(`${link(companyId, linkId)}/enabled${toQuery({ enabled: String(enabled) })}`, {});

export const requestFullSync = ({ companyId, linkId }: { companyId: string; linkId: string }) =>
  api.post<IntegrationLink>(`${link(companyId, linkId)}/full-sync`, {});

/** The first sync, once (R0036): the app updates the shop, or the shop fills the app. */
export const chooseFirstSync = ({
  companyId,
  linkId,
  direction,
}: {
  companyId: string;
  linkId: string;
  direction: FirstSyncDirection;
}): Promise<IntegrationLink> =>
  api.post<IntegrationLink>(`${link(companyId, linkId)}/first-sync`, { direction });

/** Fill the app from the e-shop again — creates what the app lacks, never overwrites. */
export const requestIntegrationImport = ({
  companyId,
  linkId,
}: {
  companyId: string;
  linkId: string;
}): Promise<IntegrationLink> => api.post<IntegrationLink>(`${link(companyId, linkId)}/import`, {});

export const listUnlistedProducts = (companyId: string, linkId: string): Promise<Array<UnlistedProduct>> =>
  api.get<Array<UnlistedProduct>>(`${link(companyId, linkId)}/unlisted-products`);

export const listIntegrationListings = (
  companyId: string,
  linkId: string,
): Promise<Array<IntegrationListing>> =>
  api.get<Array<IntegrationListing>>(`${link(companyId, linkId)}/listings`);

/** `action`: publish, unpublish or take from the shop (`import`) the selection; answers how many it concerned. */
export const changeListings = ({
  companyId,
  linkId,
  action,
  selection,
}: {
  companyId: string;
  linkId: string;
  action: 'publish' | 'unpublish' | 'import';
  selection: ListingSelection;
}): Promise<{ count: number }> =>
  api.post<{ count: number }>(`${link(companyId, linkId)}/listings/${action}`, selection);

export const resendListings = ({
  companyId,
  linkId,
}: {
  companyId: string;
  linkId: string;
}): Promise<{ count: number }> =>
  api.post<{ count: number }>(`${link(companyId, linkId)}/listings/resend`, {});

export const listProductListings = (companyId: string, productId: string): Promise<Array<ProductListing>> =>
  api.get<Array<ProductListing>>(`${company(companyId)}/products/${encodeURIComponent(productId)}/listings`);

export const listIntegrationMappings = (
  companyId: string,
  linkId: string,
): Promise<Array<IntegrationMapping>> =>
  api.get<Array<IntegrationMapping>>(`${link(companyId, linkId)}/mappings`);

export const decideMapping = ({
  companyId,
  linkId,
  mappingId,
  decision,
}: {
  companyId: string;
  linkId: string;
  mappingId: string;
  decision: MappingDecision;
}): Promise<IntegrationMapping> =>
  api.put<IntegrationMapping>(
    `${link(companyId, linkId)}/mappings/${encodeURIComponent(mappingId)}`,
    decision,
  );

export const searchIntegrationLog = (
  companyId: string,
  linkId: string,
  search: {
    flow?: LogDirection;
    outcome?: LogOutcome;
    page: number;
    sort: LogSortKey;
    direction: SortDirection;
  },
): Promise<Paginated<IntegrationLogEntry>> =>
  api.get<Paginated<IntegrationLogEntry>>(`${link(companyId, linkId)}/log${toQuery(search)}`);

export const searchIntegrationConflicts = (
  companyId: string,
  linkId: string,
  {
    open,
    ...search
  }: { open: boolean; kind?: ConflictKind; page: number; sort: ConflictSortKey; direction: SortDirection },
): Promise<Paginated<IntegrationConflict>> =>
  api.get<Paginated<IntegrationConflict>>(
    `${link(companyId, linkId)}/conflicts${toQuery({ ...search, open: open ? 'true' : undefined })}`,
  );

export const resolveIntegrationConflict = ({
  companyId,
  linkId,
  conflictId,
}: {
  companyId: string;
  linkId: string;
  conflictId: string;
}): Promise<void> =>
  api.post<void>(`${link(companyId, linkId)}/conflicts/${encodeURIComponent(conflictId)}/resolve`, {});

const gallery = (companyId: string, productId: string): string => `${product(companyId, productId)}/images`;

export const listProductImages = (companyId: string, productId: string): Promise<Array<GalleryImage>> =>
  api.get<Array<GalleryImage>>(gallery(companyId, productId));

/** One file per request, so each has its own progress and its own refusal; answers the whole gallery. */
export const uploadProductImage = (
  companyId: string,
  productId: string,
  file: File,
  onProgress: (sent: number) => void,
): Promise<Array<GalleryImage>> => {
  const body = new FormData();

  body.append('files', file, file.name);

  return api.upload<Array<GalleryImage>>(gallery(companyId, productId), body, onProgress);
};

export const reorderProductImages = (
  companyId: string,
  productId: string,
  ids: Array<string>,
): Promise<Array<GalleryImage>> =>
  api.put<Array<GalleryImage>>(`${gallery(companyId, productId)}/order`, { ids });

export const setMainProductImage = (
  companyId: string,
  productId: string,
  id: string,
): Promise<Array<GalleryImage>> =>
  api.post<Array<GalleryImage>>(`${gallery(companyId, productId)}/${encodeURIComponent(id)}/main`, {});

export const updateProductImage = (
  companyId: string,
  productId: string,
  id: string,
  input: GalleryImageInput,
): Promise<Array<GalleryImage>> =>
  api.patch<Array<GalleryImage>>(`${gallery(companyId, productId)}/${encodeURIComponent(id)}`, input);

export const removeProductImage = (
  companyId: string,
  productId: string,
  id: string,
): Promise<Array<GalleryImage>> =>
  api.delete<Array<GalleryImage>>(`${gallery(companyId, productId)}/${encodeURIComponent(id)}`);

export const searchShopOrders = (
  companyId: string,
  search: {
    linkId?: string;
    productId?: string;
    status?: OrderStatus;
    placedFrom?: string;
    placedTo?: string;
    search?: string;
    page: number;
    sort: OrderSortKey;
    direction: SortDirection;
  },
): Promise<Paginated<ShopOrderSummary>> =>
  api.get<Paginated<ShopOrderSummary>>(`${company(companyId)}/orders${toQuery(search)}`);

export const readShopOrder = (companyId: string, orderId: string): Promise<ShopOrderDetail> =>
  api.get<ShopOrderDetail>(`${company(companyId)}/orders/${encodeURIComponent(orderId)}`);

export const listOrderShops = (companyId: string): Promise<Array<OrderShop>> =>
  api.get<Array<OrderShop>>(`${company(companyId)}/orders/shops`);
const categories = (companyId: string): string => `${company(companyId)}/categories`;

/** The tree: top categories in order, each with its children. Every write answers with the whole tree. */
export const listCategories = (companyId: string): Promise<Array<Category>> =>
  api.get<Array<Category>>(categories(companyId));

export const createCategory = (companyId: string, input: CategoryInput): Promise<Array<Category>> =>
  api.post<Array<Category>>(categories(companyId), input);

/** `parentId` given moves the category with everything under it — null to the top. */
export const updateCategory = (
  companyId: string,
  categoryId: string,
  changes: CategoryInput,
): Promise<Array<Category>> =>
  api.patch<Array<Category>>(`${categories(companyId)}/${encodeURIComponent(categoryId)}`, changes);

/** Refused with children; with products only when they are to move to the parent. */
export const deleteCategory = (
  companyId: string,
  categoryId: string,
  moveProductsToParent: boolean,
): Promise<Array<Category>> =>
  api.delete<Array<Category>>(
    `${categories(companyId)}/${encodeURIComponent(categoryId)}${moveProductsToParent ? '?products=moveToParent' : ''}`,
  );

const brands = (companyId: string): string => `${company(companyId)}/brands`;

export const listBrands = (companyId: string): Promise<Array<Brand>> =>
  api.get<Array<Brand>>(brands(companyId));

export const createBrand = (companyId: string, input: BrandInput): Promise<Brand> =>
  api.post<Brand>(brands(companyId), input);

export const updateBrand = (companyId: string, brandId: string, changes: BrandInput): Promise<Brand> =>
  api.patch<Brand>(`${brands(companyId)}/${encodeURIComponent(brandId)}`, changes);

export const deleteBrand = (companyId: string, brandId: string): Promise<void> =>
  api.delete<void>(`${brands(companyId)}/${encodeURIComponent(brandId)}`);

export const uploadBrandLogo = (
  companyId: string,
  brandId: string,
  file: File,
  onProgress: (sent: number) => void,
): Promise<Brand> => {
  const body = new FormData();

  body.append('file', file, file.name);

  return api.upload<Brand>(`${brands(companyId)}/${encodeURIComponent(brandId)}/logo`, body, onProgress);
};

export const removeBrandLogo = (companyId: string, brandId: string): Promise<Brand> =>
  api.delete<Brand>(`${brands(companyId)}/${encodeURIComponent(brandId)}/logo`);

const tags = (companyId: string): string => `${company(companyId)}/tags`;

export const listTags = (companyId: string): Promise<Array<Tag>> => api.get<Array<Tag>>(tags(companyId));

/** Names by language; the default language's is required. */
export const createTag = (companyId: string, names: TextChanges): Promise<Tag> =>
  api.post<Tag>(tags(companyId), { translations: asTranslations(names, 'name') });

export const updateTag = (companyId: string, tagId: string, names: TextChanges): Promise<Tag> =>
  api.patch<Tag>(`${tags(companyId)}/${encodeURIComponent(tagId)}`, {
    translations: asTranslations(names, 'name'),
  });

export const deleteTag = (companyId: string, tagId: string): Promise<void> =>
  api.delete<void>(`${tags(companyId)}/${encodeURIComponent(tagId)}`);

const featureGroup = (companyId: string, groupId: string): string =>
  `${company(companyId)}/feature-groups/${encodeURIComponent(groupId)}`;

export const listFeatureGroups = (companyId: string): Promise<Array<FeatureGroup>> =>
  api.get<Array<FeatureGroup>>(`${company(companyId)}/feature-groups`);

export const createFeatureGroup = (companyId: string, names: TextChanges): Promise<FeatureGroup> =>
  api.post<FeatureGroup>(`${company(companyId)}/feature-groups`, {
    translations: asTranslations(names, 'name'),
  });

export const updateFeatureGroup = (
  companyId: string,
  groupId: string,
  changes: { names?: TextChanges; position?: number },
): Promise<FeatureGroup> =>
  api.patch<FeatureGroup>(featureGroup(companyId, groupId), {
    translations: asTranslations(changes.names, 'name'),
    position: changes.position,
  });

export const deleteFeatureGroup = (companyId: string, groupId: string): Promise<void> =>
  api.delete<void>(featureGroup(companyId, groupId));

export const createFeatureValue = (
  companyId: string,
  groupId: string,
  texts: TextChanges,
): Promise<FeatureGroup> =>
  api.post<FeatureGroup>(`${featureGroup(companyId, groupId)}/values`, {
    translations: asTranslations(texts, 'value'),
  });

export const updateFeatureValue = (
  companyId: string,
  groupId: string,
  valueId: string,
  changes: { texts?: TextChanges; position?: number },
): Promise<FeatureGroup> =>
  api.patch<FeatureGroup>(`${featureGroup(companyId, groupId)}/values/${encodeURIComponent(valueId)}`, {
    translations: asTranslations(changes.texts, 'value'),
    position: changes.position,
  });

export const deleteFeatureValue = (
  companyId: string,
  groupId: string,
  valueId: string,
): Promise<FeatureGroup> =>
  api.delete<FeatureGroup>(`${featureGroup(companyId, groupId)}/values/${encodeURIComponent(valueId)}`);

const transfers = (companyId: string): string => `${company(companyId)}/transfers`;

/** R0037: the company's exports and imports, newest first by default. */
export const searchTransfers = (
  companyId: string,
  search: {
    direction?: TransferDirection;
    status?: TransferStatus;
    page: number;
    sort: TransferSortKey;
    order: SortDirection;
  },
): Promise<Paginated<DataTransfer>> =>
  api.get<Paginated<DataTransfer>>(`${transfers(companyId)}${toQuery(search)}`);

export const readTransfer = (companyId: string, transferId: string): Promise<DataTransfer> =>
  api.get<DataTransfer>(`${transfers(companyId)}/${encodeURIComponent(transferId)}`);

export const searchTransferItems = (
  companyId: string,
  transferId: string,
  search: {
    match?: TransferItemMatch;
    outcome?: TransferItemOutcome;
    search?: string;
    page: number;
    sort: TransferItemSortKey;
    order: SortDirection;
  },
): Promise<Paginated<DataTransferItem>> =>
  api.get<Paginated<DataTransferItem>>(
    `${transfers(companyId)}/${encodeURIComponent(transferId)}/items${toQuery(search)}`,
  );

export const startExport = (
  companyId: string,
  input: { scope: { all: true } | { categoryId: string }; includeMedia: boolean },
): Promise<DataTransfer> => api.post<DataTransfer>(`${transfers(companyId)}/exports`, input);

/** The deployment's largest import package (R0042) — set in its stack, not in this build. */
export const transferLimits = (companyId: string): Promise<TransferLimits> =>
  api.get<TransferLimits>(`${transfers(companyId)}/limits`);

export const startImport = (
  companyId: string,
  input: { fileName: string; bytes: number },
): Promise<DataTransfer> => api.post<DataTransfer>(`${transfers(companyId)}/imports`, input);

/** The import's package, uploaded through the API with its progress (R0041); its check is queued. */
export const uploadPackage = (
  companyId: string,
  transferId: string,
  file: File,
  onProgress: (sent: number) => void,
): Promise<DataTransfer> =>
  api.upload<DataTransfer>(
    `${transfers(companyId)}/${encodeURIComponent(transferId)}/package`,
    file,
    onProgress,
    'PUT',
  );

export const applyTransfer = (
  companyId: string,
  transferId: string,
  input: { mode: TransferMode; stock: TransferStockMode },
): Promise<DataTransfer> =>
  api.post<DataTransfer>(`${transfers(companyId)}/${encodeURIComponent(transferId)}/apply`, input);

/** A one-time link that downloads a ready export through the API (R0041). */
export const transferDownload = (companyId: string, transferId: string): Promise<DownloadLink> =>
  api.get<DownloadLink>(`${transfers(companyId)}/${encodeURIComponent(transferId)}/download`);

const contacts = (companyId: string): string => `${company(companyId)}/contacts`;

const contact = (companyId: string, contactId: string): string =>
  `${contacts(companyId)}/${encodeURIComponent(contactId)}`;

/** R0039: the company's contacts, a page at a time, with the countries the filter offers. */
export const searchContacts = (
  companyId: string,
  search: {
    search?: string;
    kind?: ContactKind;
    type?: ContactType;
    labelId?: string;
    country?: string;
    doNotContact?: 'true' | 'false';
    page: number;
    sort: ContactSortKey;
    order: SortDirection;
  },
): Promise<ContactPage> => api.get<ContactPage>(`${contacts(companyId)}${toQuery(search)}`);

export const readContact = (companyId: string, contactId: string): Promise<ContactDetail> =>
  api.get<ContactDetail>(contact(companyId, contactId));

export const createContact = (companyId: string, input: ContactInput): Promise<ContactDetail> =>
  api.post<ContactDetail>(contacts(companyId), input);

/** The whole card: every list replaces the contact's. */
export const saveContact = (
  companyId: string,
  contactId: string,
  input: ContactInput,
): Promise<ContactDetail> => api.put<ContactDetail>(contact(companyId, contactId), input);

export const deleteContact = (companyId: string, contactId: string): Promise<void> =>
  api.delete<void>(contact(companyId, contactId));

export const readContactHistory = (
  companyId: string,
  contactId: string,
  page: number,
): Promise<Paginated<ContactHistoryEntry>> =>
  api.get<Paginated<ContactHistoryEntry>>(`${contact(companyId, contactId)}/history${toQuery({ page })}`);

export const listContactNotes = (companyId: string, contactId: string): Promise<Array<ContactNote>> =>
  api.get<Array<ContactNote>>(`${contact(companyId, contactId)}/notes`);

export const addContactNote = (companyId: string, contactId: string, body: string): Promise<ContactNote> =>
  api.post<ContactNote>(`${contact(companyId, contactId)}/notes`, { body });

export const editContactNote = (
  companyId: string,
  contactId: string,
  noteId: string,
  body: string,
): Promise<ContactNote> =>
  api.patch<ContactNote>(`${contact(companyId, contactId)}/notes/${encodeURIComponent(noteId)}`, { body });

export const deleteContactNote = (companyId: string, contactId: string, noteId: string): Promise<void> =>
  api.delete<void>(`${contact(companyId, contactId)}/notes/${encodeURIComponent(noteId)}`);

const contactLabels = (companyId: string): string => `${company(companyId)}/contact-labels`;

export const listContactLabels = (companyId: string): Promise<Array<ContactLabel>> =>
  api.get<Array<ContactLabel>>(contactLabels(companyId));

export const createContactLabel = (companyId: string, name: string): Promise<ContactLabel> =>
  api.post<ContactLabel>(contactLabels(companyId), { name });

export const renameContactLabel = (companyId: string, labelId: string, name: string): Promise<ContactLabel> =>
  api.patch<ContactLabel>(`${contactLabels(companyId)}/${encodeURIComponent(labelId)}`, { name });

export const deleteContactLabel = (companyId: string, labelId: string): Promise<void> =>
  api.delete<void>(`${contactLabels(companyId)}/${encodeURIComponent(labelId)}`);

const contactPackage = (companyId: string): string => `${company(companyId)}/contact-package`;

/** Every contact of the company as a ZIP. */
export const exportContacts = (companyId: string): Promise<Blob> => api.blob(contactPackage(companyId));

const packageForm = (file: File): FormData => {
  const body = new FormData();

  body.append('file', file, file.name);

  return body;
};

/** What importing the file would do, row by row. Nothing is written. */
export const previewContactImport = (
  companyId: string,
  file: File,
  onProgress: (sent: number) => void,
): Promise<ContactImportPreview> =>
  api.upload<ContactImportPreview>(`${contactPackage(companyId)}/preview`, packageForm(file), onProgress);

export const importContacts = (
  companyId: string,
  file: File,
  onProgress: (sent: number) => void,
): Promise<ContactImportResult> =>
  api.upload<ContactImportResult>(`${contactPackage(companyId)}/import`, packageForm(file), onProgress);
